/* ========== SHARED CHART UTILITIES ==========
 * COLORS palette + shared draw helpers used by every chart type.
 * Each chart-type module imports from here.
 * ============================================== */

export const COLORS = [
  '#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47', '#264478', '#9B57A0',
  '#636363', '#B85450', '#6C8EBF', '#D6B656', '#82B366', '#E07B53', '#9673A6', '#23445D'
];

export var LEGEND_ROW_HEIGHT = 13;

export function drawAxes(ctx, x, y, width, height) {
  ctx.strokeStyle = '#999';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + height);
  ctx.lineTo(x + width, y + height);
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + height);
  ctx.stroke();
}

export function drawGridlines(ctx, x, y, width, height, minValue, range, numGridLines) {
  numGridLines = numGridLines || 5;
  ctx.strokeStyle = '#E0E0E0';
  ctx.lineWidth = 1;
  for (var i = 0; i <= numGridLines; i++) {
    var gy = y + height - (i / numGridLines) * height;
    ctx.beginPath();
    ctx.moveTo(x, gy);
    ctx.lineTo(x + width, gy);
    ctx.stroke();
    var val = minValue + (i / numGridLines) * range;
    ctx.fillStyle = '#888';
    ctx.font = '9px Arial';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(val % 1 === 0 ? val.toString() : val.toFixed(1), x - 4, gy);
  }
}

var WRAP_LINE_H = 11;

/**
 * Decide how the x-axis category labels must be laid out so they never
 * overlap. Excel's order of preference:
 *   1. flat   — single horizontal line when every label fits its slot
 *   2. wrap   — word-wrap onto up to 3 lines inside the slot (pivot-chart
 *               composite labels like "John Mar QA" do this)
 *   3. rotate — 45° rotation only when even a single word can't fit,
 *               thinning labels out when rotated ones would still collide
 *
 * Returns { mode, labels, lines, rotate, step, band } where band is the
 * vertical space (px) the labels consume below the axis line.
 */
export function xAxisLabelLayout(ctx, cats, numCategories, spacing) {
  var labels = [];
  var maxW = 0;
  ctx.font = '9px Arial';
  for (var i = 0; i < numCategories; i++) {
    var raw = cats && cats[i] != null && cats[i] !== '' ? '' + cats[i] : '' + (i + 1);
    labels.push(raw);
    var w = ctx.measureText(raw).width;
    if (w > maxW) maxW = w;
  }
  var avail = spacing - 4;

  // 1. Flat — everything fits on one line
  if (!(numCategories > 0) || !(spacing > 0) || maxW <= avail) {
    return {
      mode: 'flat', labels: labels,
      lines: labels.map(function (l) { return [l]; }),
      rotate: false, step: 1, band: 15,
    };
  }

  // 2. Wrap — possible when every individual word fits the slot width
  var canWrap = true;
  var wrapped = [];
  var maxLines = 1;
  for (var j = 0; j < numCategories && canWrap; j++) {
    var words = labels[j].split(/\s+/).filter(function (w2) { return w2 !== ''; });
    if (words.length === 0) { wrapped.push(['']); continue; }
    var lines = [];
    var cur = '';
    for (var k = 0; k < words.length; k++) {
      if (ctx.measureText(words[k]).width > avail) { canWrap = false; break; }
      var tryLine = cur ? cur + ' ' + words[k] : words[k];
      if (ctx.measureText(tryLine).width <= avail) {
        cur = tryLine;
      } else {
        lines.push(cur);
        cur = words[k];
      }
    }
    if (!canWrap) break;
    if (cur) lines.push(cur);
    if (lines.length > 3) lines = lines.slice(0, 3);
    wrapped.push(lines);
    if (lines.length > maxLines) maxLines = lines.length;
  }
  if (canWrap) {
    return {
      mode: 'wrap', labels: labels, lines: wrapped,
      rotate: false, step: 1, band: 6 + maxLines * WRAP_LINE_H + 2,
    };
  }

  // 3. Rotate — truncate very long labels so the band stays reasonable
  var maxChars = 14;
  var tLabels = labels.map(function (l) {
    return l.length > maxChars ? l.slice(0, maxChars - 1) + '…' : l;
  });
  var truncW = 0;
  for (var t = 0; t < tLabels.length; t++) {
    var tw = ctx.measureText(tLabels[t]).width;
    if (tw > truncW) truncW = tw;
  }
  return {
    mode: 'rotate', labels: tLabels, lines: null, rotate: true,
    step: spacing >= 11 ? 1 : Math.ceil(11 / Math.max(1, spacing)),
    band: Math.ceil(truncW * 0.71) + 10,
  };
}

/** Truncate a label with an ellipsis so it fits maxW pixels (9px font). */
function fitLabel(ctx, label, maxW) {
  if (ctx.measureText(label).width <= maxW) return label;
  var l = label;
  while (l.length > 1 && ctx.measureText(l + '…').width > maxW) {
    l = l.slice(0, -1);
  }
  return l + '…';
}

export var MULTI_LEVEL_ROW_HEIGHT = 14;

/**
 * Multi-level category axis (Excel multiLvlStrCache): each level renders
 * on its own row below the axis — level 0 (most detailed, e.g. "Sunday")
 * closest to the axis, parent levels ("2020", "Jan") underneath — with
 * light vertical separators between category slots, exactly like Excel's
 * boxed multi-row axis. Labels in higher levels that repeat across
 * consecutive slots are merged and centered over their span.
 * Returns the vertical band (px) consumed.
 */
export function drawMultiLevelXAxisLabels(ctx, x, yBase, levels, numCategories, slotW) {
  var numLevels = levels.length;
  var band = 6 + numLevels * MULTI_LEVEL_ROW_HEIGHT + 2;

  // Vertical slot separators spanning the whole label band
  ctx.strokeStyle = '#D9D9D9';
  ctx.lineWidth = 1;
  for (var k = 0; k <= numCategories; k++) {
    var sx = x + k * slotW;
    ctx.beginPath();
    ctx.moveTo(sx, yBase);
    ctx.lineTo(sx, yBase + band - 2);
    ctx.stroke();
  }

  ctx.font = '9px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (var li = 0; li < numLevels; li++) {
    var rowY = yBase + 6 + li * MULTI_LEVEL_ROW_HEIGHT + MULTI_LEVEL_ROW_HEIGHT / 2;
    var labels = levels[li] || [];
    var i = 0;
    while (i < numCategories) {
      var label = labels[i] != null ? '' + labels[i] : '';
      // Merge consecutive repeats (Excel writes parent labels once per span)
      var j = i;
      while (j + 1 < numCategories && ('' + (labels[j + 1] != null ? labels[j + 1] : '')) === label) j++;
      if (label) {
        var spanW = (j - i + 1) * slotW;
        var cx = x + i * slotW + spanW / 2;
        ctx.fillStyle = '#666';
        ctx.fillText(fitLabel(ctx, label, spanW - 4), cx, rowY);
      }
      i = j + 1;
    }
  }
  return band;
}

/**
 * Draw x-axis labels with automatic 45° rotation + thinning when they
 * would overlap. `centered` places each label mid-slot (column charts);
 * otherwise labels sit at the data point (line charts).
 * When `catLevels` (multi-level categories) is provided, delegates to the
 * Excel-style stacked-row layout instead.
 * Returns the vertical band (px) consumed, so callers can place the
 * legend underneath.
 */
export function drawXAxisLabels(ctx, x, yBase, cats, numCategories, spacing, centered, catLevels) {
  if (!numCategories || numCategories <= 0 || !(spacing > 0)) return 15;
  if (catLevels && catLevels.length > 1) {
    // Multi-level axes are always slot-based: derive slot width from the
    // point spacing when the caller is point-aligned (line charts).
    var slotW = centered ? spacing : (spacing * Math.max(1, numCategories - 1)) / numCategories;
    return drawMultiLevelXAxisLabels(ctx, x, yBase, catLevels, numCategories, slotW);
  }
  var layout = xAxisLabelLayout(ctx, cats, numCategories, spacing);
  ctx.font = '9px Arial';
  for (var i = 0; i < numCategories; i += layout.step) {
    var tickX = centered ? x + i * spacing + spacing / 2 : x + i * spacing;
    ctx.fillStyle = '#666';
    if (layout.rotate) {
      ctx.save();
      ctx.translate(tickX, yBase + 5);
      ctx.rotate(-Math.PI / 4);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(layout.labels[i], 0, 0);
      ctx.restore();
    } else {
      // flat or wrapped: one or more horizontal lines per category
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      var lines = layout.lines ? layout.lines[i] : [layout.labels[i]];
      for (var li = 0; li < lines.length; li++) {
        ctx.fillText(lines[li], tickX, yBase + 6 + li * 11);
      }
    }
  }
  return layout.band;
}

export function drawCategoryLabels(ctx, x, y, height, cats, numCategories, groupWidth, catLevels) {
  return drawXAxisLabels(ctx, x, y + height, cats, numCategories, groupWidth, true, catLevels);
}

/**
 * Pack legend entries into rows so no row is wider than maxWidth.
 */
export function computeLegendRows(ctx, seriesArr, maxWidth) {
  if (!seriesArr || seriesArr.length === 0) return [];
  ctx.font = '9px Calibri, Arial';
  var rows = [];
  var current = [];
  var currentW = 0;
  seriesArr.forEach(function (s, i) {
    var w = 14 + ctx.measureText(s.name || '').width + 16;
    if (current.length > 0 && currentW + w > maxWidth) {
      rows.push(current);
      current = [];
      currentW = 0;
    }
    current.push({
      name: s.name || '',
      color: s.color || COLORS[i % COLORS.length],
      width: w,
      idx: i,
    });
    currentW += w;
  });
  if (current.length > 0) rows.push(current);
  return rows;
}

export function drawLegend(ctx, x, y, width, height, seriesArr, options) {
  // Excel hides the legend by default for single-series charts.
  if (!seriesArr || seriesArr.length < 2) return;
  var isLine = options && options.lineStyle;
  // Sit below the x-axis label band (rotated labels eat more space).
  var labelBand = (options && options.labelBand) || 15;
  ctx.font = '9px Calibri, Arial';
  ctx.textBaseline = 'middle';

  var rows = computeLegendRows(ctx, seriesArr, width);
  var firstRowY = y + height + labelBand + 7;

  rows.forEach(function (row, rowI) {
    var rowWidth = row.reduce(function (s, it) { return s + it.width; }, 0);
    var legendX = x + (width - rowWidth) / 2;
    var legendY = firstRowY + rowI * LEGEND_ROW_HEIGHT;
    row.forEach(function (item) {
      var color = item.color;
      if (isLine) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(legendX, legendY);
        ctx.lineTo(legendX + 10, legendY);
        ctx.stroke();
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(legendX + 5, legendY, 2.5, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillStyle = color;
        ctx.fillRect(legendX, legendY - 5, 10, 10);
      }
      ctx.fillStyle = '#333';
      ctx.textAlign = 'left';
      ctx.fillText(item.name, legendX + 14, legendY);
      legendX += item.width;
    });
  });
}

export function getMinMaxRange(seriesArr) {
  var allVals = seriesArr.flatMap(function (s) { return s.data; });
  var maxValue = Math.max.apply(null, allVals.concat([0])) || 100;
  var minValue = Math.min.apply(null, allVals.concat([0]));
  var range = maxValue - minValue || 100;
  return { minValue: minValue, maxValue: maxValue, range: range };
}

/** Darken (negative percent) or lighten (positive percent) a hex color. */
export function shadeColor(hex, percent) {
  var m = hex.replace('#', '');
  var num = parseInt(m, 16);
  var r = (num >> 16) + Math.round(255 * percent / 100);
  var g = ((num >> 8) & 0xff) + Math.round(255 * percent / 100);
  var b = (num & 0xff) + Math.round(255 * percent / 100);
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}
